import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { EMPTY, merge, Observable } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, take } from 'rxjs/operators';
import { LoadableArrayLoadStrategy, LoadableHelper, LoadableStatus, Logger, TrackService } from '@aiwyn/shared';
import { LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { GetClientRequestDetails } from '@aiwyn/common/client-requests';
import { CxpTasksSelectors } from '../state/cxp-tasks-state.selectors';
import { LoadCxpTasks } from '../state/cxp-tasks.actions';
import { LoadRequestDetail, SetSelectedRequestId } from '../state/cxp-crl-detail.actions';
import {
  CxpFilterGroup,
  CxpTask,
  CxpTaskFilterSelection,
  CxpTaskStatus,
} from '../models';
import { ClientRequestsBridgeService } from './client-requests-bridge.service';
import { CxpRouteParamsService } from './cxp-route-params.service';
import { applyTaskFilters, buildFilterGroups } from './utils/cxp-task-filters.utils';
// OLD MODULE DEPENDENCY: imports from legacy ClientRequestsState.
// Remove when CXP owns its own detail view.
import { ClientRequestsState, InitializeClientRequests, SetSelectedClientRequestId } from '../../client-requests/state';

@Injectable({ providedIn: 'root' })
export class CxpTasksDataStateService {
  private readonly store = inject(Store);
  private readonly logger = inject(Logger);
  private readonly bridge = inject(ClientRequestsBridgeService);
  private readonly trackService = inject(TrackService);
  private readonly launchDarklyService = inject(LaunchDarklyService);
  // Injected to bootstrap route param tracking — the service's constructor sets up NavigationEnd subscriptions.
  private readonly _routeParams = inject(CxpRouteParamsService);

  readonly tasks = this.store.selectSignal(CxpTasksSelectors.getTasks);
  readonly isLoading = this.store.selectSignal(CxpTasksSelectors.isLoading);
  readonly isLoaded = this.store.selectSignal(CxpTasksSelectors.isLoaded);
  readonly totalCount = this.store.selectSignal(CxpTasksSelectors.getTotalTaskCount);
  readonly openCount = this.store.selectSignal(CxpTasksSelectors.getOpenTaskCount);
  readonly staff = this.store.selectSignal(CxpTasksSelectors.getStaff);
  readonly permissions = this.store.selectSignal(CxpTasksSelectors.getPermissions);
  /**
   * Combines the current enriched task list with the caller's filter
   * selection and an optional search query, returning the matching subset.
   * Reads `tasks()` internally, so when called inside a `computed()` the
   * consumer's reactivity is automatically tracked against this facade's
   * signal as well as whichever signals the caller passes in for `filters`
   * and `searchQuery`.
   */
  filterTasks(filters: CxpTaskFilterSelection, searchQuery?: string): CxpTask[] {
    return applyTaskFilters(this.tasks(), filters, searchQuery);
  }

  /**
   * Builds the sidebar filter groups (service-line + account) scoped to the
   * given tab. Counts reflect ONLY the tab — search and chip selection do
   * not narrow the chip totals (otherwise the chip set would collapse as
   * the user filters).
   */
  filterGroupsFor(tab: CxpTaskStatus): CxpFilterGroup[] {
    return buildFilterGroups(this.tasks().filter((task) => task.status === tab));
  }

  /**
   * Combine observables that react to NGXS state changes and dispatch actions as side effects.
   * The caller is responsible for subscribing and managing the subscription lifetime (e.g. takeUntilDestroyed).
   * The caller is also responsible for logging and handling errors.
   */
  backgroundEffects$(): Observable<unknown> {
    return merge(
      this._loadTasksIfNeeded$().pipe(catchError(() => EMPTY)),
      this._prewarmLegacyStateOnLoaded$().pipe(catchError(() => EMPTY)),
      this._syncLegacyStateOnRequestSelected$().pipe(catchError(() => EMPTY)),
    );
  }

  /**
   * Watches task loading status and dispatches LoadCxpTasks when the state needs requesting.
   * Re-triggers if status resets to DEFAULT (e.g., on state clear after navigation away).
   */
  private _loadTasksIfNeeded$(): Observable<unknown> {
    return this.store.select(CxpTasksSelectors.slices.status).pipe(
      distinctUntilChanged(),
      filter((status) => LoadableHelper.shouldRequest(status)),
      switchMap(() => this.store.dispatch(new LoadCxpTasks())),
      catchError((error) => {
        this.logger.error('[CxpTasksDataStateService] _loadTasksIfNeeded$ failed:', error);
        return EMPTY;
      }),
    );
  }

  /**
   * Once CXP tasks are loaded, speculatively initialize the legacy ClientRequestsState
   * so that groups + secondary data are ready by the time the user clicks a task card.
   * distinctUntilChanged prevents re-emission while isLoaded stays true;
   * if state resets (LOADED→DEFAULT→LOADED), it re-fires correctly.
   */
  private _prewarmLegacyStateOnLoaded$(): Observable<unknown> {
    return this.store.select(CxpTasksSelectors.isLoaded).pipe(
      distinctUntilChanged(),
      filter(Boolean),
      switchMap(() => {
        this.bridge.prewarmLegacyState();
        this.trackService.track('CXP Tasks Page Viewed', {
          openCount: this.openCount(),
          totalCount: this.totalCount(),
        });
        return EMPTY;
      }),
      catchError((error) => {
        this.logger.error('[CxpTasksDataStateService] _prewarmLegacyStateOnLoaded$ failed:', error);
        return EMPTY;
      }),
    );
  }

  /**
   * When selectedRequestId changes (route-params dispatched it to state),
   * drive the detail surface that `CXP_ENABLE_REQUEST_LIST_REDESIGN` selects.
   * The legacy state is driven either way — the redesigned page still falls
   * back to it for surfaces CXP does not own yet.
   *
   * Flow:
   * 1. Redesign ON only: set selectedRequestId + trigger LoadRequestDetail
   *    (via the API bridge) so detailData populates for the redesigned page.
   *    With the flag off this must not fire — it would call the REST detail
   *    endpoints for a page that never renders.
   * 2. Legacy (OLD MODULE DEPENDENCY): fire-and-forget InitializeClientRequests
   *    (pre-warmed, usually fast/no-op).
   * 3. Force-refresh legacy details for this request (REFRESH strategy).
   * 4. Wait for legacy status === LOADED.
   * 5. Dispatch SetSelectedClientRequestId to load details/metadata/questionnaire.
   * 6. Track the view event.
   *
   * When requestId becomes null (navigated back to list), the effect stops via distinctUntilChanged + filter.
   */
  private _syncLegacyStateOnRequestSelected$(): Observable<unknown> {
    return this.store.select(CxpTasksSelectors.getSelectedRequestId).pipe(
      distinctUntilChanged(),
      filter((requestId): requestId is number => requestId !== null),
      switchMap((requestId) =>
        this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.CXP_ENABLE_REQUEST_LIST_REDESIGN).pipe(
          take(1),
          switchMap((redesignEnabled) => {
            // New CXP CRL detail state: keep its selection in sync and load detail
            // through the API bridge (independent of the legacy path below).
            if (redesignEnabled) {
              this.store.dispatch(new SetSelectedRequestId(requestId));
              this.store.dispatch(new LoadRequestDetail(requestId));
            }

            // Fire-and-forget: ensure legacy state is initialized
            this.store.dispatch(new InitializeClientRequests());
            // REFRESH (not REPLACE) — REPLACE clears items mid-load and trips
            // the legacy panel's `data.id` access (common-client-request.ts:242).
            this.store.dispatch(new GetClientRequestDetails(requestId, LoadableArrayLoadStrategy.REFRESH));

            // Wait for legacy state to be LOADED, then select the specific request
            return this.store.select(ClientRequestsState.getSlices.status).pipe(
              filter((status) => status === LoadableStatus.LOADED),
              take(1),
              switchMap(() => this.store.dispatch(new SetSelectedClientRequestId(requestId))),
              switchMap(() => {
                this.trackService.track('CXP Task Detail Viewed', { requestId });
                return EMPTY;
              }),
            );
          }),
        ),
      ),
      catchError((error) => {
        this.logger.error('[CxpTasksDataStateService] _syncLegacyStateOnRequestSelected$ failed:', error);
        return EMPTY;
      }),
    );
  }
}
